<template>
    <div>
        <h1 class="text-2xl font-bold mb-8">Resume your subscription</h1>
        <BillingSection title="Current Subscription">
            <div
                v-if="errorMessage"
                role="alert"
                class="flex flex-col gap-2 rounded-md bg-red-50 p-4 mb-4 text-red-700"
            >
                <p class="font-bold">{{ errorTitle }}</p>
                <p class="text-sm">{{ errorMessage }}</p>
            </div>
            <div
                class="space-y-2.5 bg-blue-50 p-6"
                v-if="subscription !== null"
            >
                <div class="plan-details flex flex-col">
                    <div class="font-bold text-lg">
                        {{ subscription.title }}
                    </div>
                    <div class="flex-grow my-2"></div>
                    <div class="text-lg">
                        ${{ formatPrice(subscription.price) }} per month
                    </div>
                    <p class="text-small tracking-tight my-2">
                        This plan will no longer be canceled. It will renew on
                        {{ formatDate(subscription.current_period_end) }}.
                    </p>
                    <div
                        class="flex flex-col sm:flex-row items-center flex-1 gap-2 mt-6"
                    >
                        <Link
                            href="/account/billing"
                            intent="flat"
                            class="hover:bg-blue-100"
                        >
                            Go Back
                        </Link>
                        <Button
                            intent="primary"
                            :disabled="processing"
                            v-on:click="renewPlan"
                            class="flex gap-2 items-center"
                        >
                            Resume plan
                            <LoadingSpinner
                                color="light"
                                placement="button"
                                :show="processing"
                            />
                        </Button>
                    </div>
                    <div class="mt-4">
                        <p class="text-xs tracking-tight">
                            By renewing your plan, you agree to Clover
                            Learning's
                            <strong
                                ><a
                                    href="https://cloverlearning.com/terms-of-service"
                                    target="_blank"
                                    >Terms of Service</a
                                ></strong
                            >
                            and
                            <strong
                                ><a
                                    href="https://cloverlearning.com/privacy"
                                    target="_blank"
                                    >Privacy Policy</a
                                ></strong
                            >.
                        </p>
                    </div>
                </div>
            </div>
        </BillingSection>
    </div>
</template>

<script lang="ts">
import { mapState } from "vuex";
import BillingSection from "./BillingSection.vue";
import { getSubscription, renewSubscription } from "../../../api/billing";
import { format, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import Button from "@/components/ui/ButtonLinks/Button.vue";
import ButtonLink from "@/components/ui/ButtonLinks/Link.vue";
import Link from "@/components/ui/ButtonLinks/Link.vue";
import { getApiErrorMessage } from "@/api/errors";

export default {
    components: { BillingSection, Button, ButtonLink, Link },
    props: {},
    data(): any {
        return {
            subscriptionId:
                // @ts-ignore
                window.location.pathname.match(/sub_[a-zA-Z0-9]+/)[0],
            subscription: null,
            processing: false,
            errorTitle: "",
            errorMessage: null,
        };
    },
    computed: {
        ...mapState(["user"]),
    },
    methods: {
        formatPrice(price) {
            return (price / 100).toFixed(2);
        },
        formatDate(date) {
            let addChar = date.charAt(date.length - 1) === "Z" ? "" : "Z";
            return format(
                utcToZonedTime(parseISO(date + addChar), this.user.timezone),
                "MMMM d, yyyy"
            );
        },
        renewPlan() {
            this.processing = true;
            this.errorMessage = null;
            renewSubscription(this.user.uuid, this.subscriptionId)
                .then(() => {
                    this.processing = false;
                    // @ts-ignore
                    window.location = "/account/billing";
                })
                .catch((e) => {
                    this.processing = false;
                    this.errorTitle = "We couldn't resume your subscription.";
                    this.errorMessage = getApiErrorMessage(e);
                });
        },
    },
    mounted() {
        getSubscription(this.user.uuid, this.subscriptionId)
            .then((subscription) => {
                this.subscription = subscription;
            })
            .catch((e) => {
                this.errorTitle = "We couldn't load your subscription details.";
                this.errorMessage = getApiErrorMessage(e);
            });
    },
};
</script>
